import React, {
  useContext, useEffect, useState, lazy, Suspense
} from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import proptypes from 'prop-types';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { Metadata } from '@thd-olt-component-react/metadata';
import {
  Accordion, AccordionBody, AccordionTitle, Overlay, Button, Row, Col
} from '@thd-olt-component-react/core-ui';
import { BackToTop } from '@thd-olt-component-react/back-to-top';
import { Alert, Typography } from '@one-thd/sui-atomic-components';
import { imageBaseUrl } from '../../constants';
import { PrivacyHeader } from './PrivacyHeader';
import { PrivacyMetadata } from './PrivacyMetadata';
import './ExerciseMyPrivacyRights.scss';
import { isLoggedIn } from '../epc/utils/general';
import {
  USER_HAS_NOT_OPTED_OUT_PREVIOUSLY,
  USER_HAS_OPTED_OUT_PREVIOUSLY
} from './constants/Constants';
import LinkButton from '../epc/components/LinkButton';

const { string } = proptypes;
// eslint-disable-next-line max-len,id-length
const ConsumerPrivacyActForm = lazy(() => import('@thd-olt-component-react/consumer-privacy-act-form').then((m) => ({ default: m.ConsumerPrivacyActForm })));

const metadata = {
  title: 'Exercise My Privacy Rights - The Home Depot',
  description: 'The Home Depot values and respects your privacy. Please use this page to request the '
    + 'personal information we collect about you, request that we delete your personal information, '
    + 'and opt out of having your personal information sold.',
  canonical: 'https://www.homedepot.com/privacy/Exercise-My-Privacy-Rights'
};

export const ExerciseMyPrivacyRights = ({ recaptchaKey }) => {

  const { channel } = useContext(ExperienceContext);

  const viewModeClassName = (channel === 'mobile')
    ? 'exercise-my-privacy-rights-mobile' : 'exercise-my-privacy-rights';

  const [isOverlayOn, setOverlay] = useState(false);
  const [hasOptOutCookie, setHasOptOutCookie] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const handleClose = () => {
    setOverlay(false);
  };
  const handleTurnOnOverlay = () => {
    setOverlay(true);
  };

  const optOutBannerText = () => {
    if (hasOptOutCookie) {
      return USER_HAS_OPTED_OUT_PREVIOUSLY;
    }
    return USER_HAS_NOT_OPTED_OUT_PREVIOUSLY;
  };

  function renderOptOutStatusBanner() {
    if (isLoggedIn()) {
      return (
        <div className="sui-rounded-base sui-py-2 sui-w-full xl:sui-w-1/2 lg:sui-w-1/2 md:sui-w-3/5 ">
          <Alert status="info" size="large">
            <Typography>{optOutBannerText()}</Typography>
          </Alert>
        </div>
      );
    }
    return null;
  }

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('exerciseMyPrivacyRights.static.page-load', {
      pageName: 'privacy and security center>submit a request',
      pageType: 'content',
      primaryCategory: 'privacy and security center',
      subCategory1: 'privacy and security center>submit a request',
      subCategory2: 'privacy and security center>submit a request'
    });
  });

  useEffect(() => {
    setIsMounted(true);

    const cookieExists = document.cookie
      .split('; ')
      .some((row) => row.startsWith('OptOutOnRequest'));

    const localValue = localStorage.getItem('HDPrivacyOptOut');

    if (cookieExists || localValue) {
      setHasOptOutCookie(true);
    }
  }, []);

  return (
    <div style={{ backgroundColor: '#f5f5f5' }} className={viewModeClassName}>
      <Metadata>
        <PrivacyMetadata
          title={metadata.title}
          description={metadata.description}
          canonical={metadata.canonical}
        />
      </Metadata>
      <script
        src="https://www.google.com/recaptcha/api.js?onload=onloadCallback&render=explicit"
        async
        defer
      />

      <div className="container">

        <QueryProvider cacheKey="customer-data-governance">
          <div className="cdg-content--header">
            <Row>
              <Col>
                <Breadcrumbs staticCrumbs={[
                  { label: 'Privacy & Security Center', url: '/privacy/overview' },
                  { label: 'Submit a Request', url: '' }
                ]}
                />
              </Col>
            </Row>
          </div>
        </QueryProvider>

        <PrivacyHeader submitARequestActive />

        <div className="cdg-content--body">
          <Row className="white margin-20">
            <Col className="content-summary">
              <Row>
                <Col>
                  <h1>
                    What to Know Before You Get Started
                  </h1>
                  <br />
                  <p style={{ fontSize: '18px' }}>If you&apos;re a resident of the United States or its territories,
                    you can make privacy rights requests. Here are your options:
                  </p>
                </Col>
              </Row>
              <Row>
                <Col>
                  <img
                    className="logo exercise-my-privacy-rights__bullet-logo"
                    width="30"
                    height="30"
                    alt="Privacy ToolBox"
                    src={imageBaseUrl + '/access-pdf.svg'}
                  />
                  <span
                    className="bullet-header exercise-my-privacy-rights__bullet-header"
                  >Access My Information
                  </span>
                  <br />
                  <p className="p-description">
                    You can request a report regarding the personal information that we collect
                    about you. Residents of Delaware, Minnesota, and Oregon also have the right to request a list of
                    specific third parties to whom we disclose personal information
                  </p>
                </Col>
              </Row>
              <Row>
                <Col>
                  <img
                    className="logo exercise-my-privacy-rights__bullet-logo"
                    width="30"
                    height="30"
                    alt="Privacy ToolBox"
                    src={imageBaseUrl + '/trash-icon.svg'}
                  />
                  <span className="bullet-header exercise-my-privacy-rights__bullet-header">
                    Delete My Information
                  </span>
                  <br />
                  <p className="p-description">
                    You can ask us to delete the personal information that we collect
                    about you and delete your personal account
                    <span style={{ fontSize: '8px', verticalAlign: 'super', top: '12px' }}>1</span>
                  </p>
                </Col>
              </Row>

              <Row>
                <Col>
                  <p>
                    <img
                      className="logo exercise-my-privacy-rights__bullet-logo"
                      width="30"
                      height="30"
                      alt="Privacy ToolBox"
                      src={imageBaseUrl + '/x-icon.svg'}
                    />
                    <span className="bullet-header exercise-my-privacy-rights__bullet-header">
                      Opt Out of Sale
                      <span style={{ fontSize: '8px', verticalAlign: 'super', top: '12px' }}>2</span>
                      &nbsp;and Sharing or Use for Targeted Advertising
                      <span style={{ fontSize: '8px', verticalAlign: 'super', top: '12px' }}>3</span>
                    </span>
                  </p>
                  <br />
                  <p className="p-description">
                    You can ask that we don&apos;t sell the personal information that we collect about
                    you and that we don’t disclose or use personal information to support targeted
                    advertising on unaffiliated or differently branded websites and applications
                  </p>
                  <Overlay
                    onClose={handleClose}
                    open={isOverlayOn}
                    medium
                    modalClass={channel === 'mobile' ? 'exercise-my-privacy-rights--modal-mobile' : 'cpa-modal'}
                    closeButton
                    positionedCloseButton
                  >
                    {/* This section is awkwardly forcing style to match style before Core UI.
                        This should be re-visited for any future revisions. */}
                    <Row>
                      <Col className="exercise-my-privacy-rights--modal-header">
                        <h1>Opt Out of Sale</h1>
                      </Col>
                    </Row>
                    <div className="exercise-my-privacy-rights--modal-divider" />
                    <Row>
                      <Col className="exercise-my-privacy-rights--modal-body">
                        <p>
                          When you visit our website, we use cookies and similar tools to
                          automatically make certain personal information available to select third
                          parties who are providing services to us to help us enhance your
                          experience, improve and deliver advertising, learn how you use the
                          website, and achieve the other purposes addressed in the “Our Tracking
                          Tools” section of our Privacy and Security Statement. Some of those
                          select third parties may use the personal information for their own
                          purposes or to provide services to other businesses. This disclosure may
                          be considered a “sale” of your personal information. If you want to opt
                          out of such automatic sharing, use this form to submit an Opt Out of Sale
                          request, and we will place a key in your local storage and a cookie on your browser to
                          automatically prevent the sharing from happening when you use that browser to visit our
                          website. Because we use a cookie to automatically identify and register
                          your preference, if you are not signed into your account and you disable cookies
                          on your browser or device, or clear your local storage, the Opt Out of Sale request
                          will no longer work. You can always enable cookies on your browser or device
                          and visit this page again to register your Opt Out of Sale request. We may
                          not recognize you if you are not signed into your account when you use other
                          browsers or devices to visit our website. So, you will need to submit
                          a separate Opt Out of Sale request on each device and browser you use to
                          visit our website if you are not signed into your account. For more
                          information about our tracking tools and how to control them, please click
                          <a
                            style={{
                              whiteSpace: 'pre'
                            }}
                            href="/privacy/privacy-and-security-statement#TrackingTools"
                          > here
                          </a>.
                        </p>
                        <br />
                        <p>
                          After you submit an Opt Out of Sale request, you may still see advertising
                          regarding our products and services. And some of that advertising may be
                          delivered by third parties or appear on third-party sites or services.
                          This advertising may be general audience advertising or may be delivered
                          by service providers in ways that do not involve sales of your personal
                          information.
                        </p>
                        <br />
                        <p>
                          When you submit your Opt Out of Sale request using the form below, as
                          indicated above, we will no longer share your information via digital
                          tracking technologies used on homedepot.com. You may need to take other
                          steps for other websites, as described in the privacy policies for those
                          websites. We also will use the information you provide via the form to
                          identify the personal information not involving online tracking
                          technologies that we hold about you so that we can honor your request that
                          such information no longer be sold as well.
                        </p>
                        <br />
                        <p>
                          Once you submit your request, we will place a key in your local storage and a  cookie
                          on your browser to automatically prevent the sharing from happening when you use the browser
                          to visit our website. However, to fully register your Opt Out of Sale
                          request for information that may be shared via channels other than online
                          tracking technologies, if any, you will need to provide a working email
                          address.
                        </p>
                        <br />
                        <p>
                          We also recognize the “Global Privacy Control” (GPC) to allow you to opt
                          out of the sale of your personal information and the sharing of your personal
                          information for targeted advertising on homedepot.com. Please see our Privacy
                          and Security Statement for more information related to using GPC on our websites.
                        </p>
                        <hr />
                      </Col>
                    </Row>
                    <Row>
                      <Col className="exercise-my-privacy-rights--modal-body">
                        <Button onClick={handleClose}>Close
                        </Button>
                      </Col>
                    </Row>
                  </Overlay>
                </Col>
              </Row>

              <Row>
                <Col>
                  <img
                    id="logo"
                    width="30"
                    height="30"
                    alt="Privacy ToolBox"
                    src={imageBaseUrl + '/limit-spi.svg'}
                    className="logo exercise-my-privacy-rights__bullet-logo"
                  />
                  <span
                    id="bullet-header"
                    className="bullet-header exercise-my-privacy-rights__bullet-header"
                  >Limit the Use of My Sensitive Personal Information
                  </span>
                  <br />
                  <p id="p-description" className="p-description">
                    You can ask that we use and disclose&nbsp;
                    <a href="/privacy/privacy-and-security-statement#SensitivePersonalInformation">
                      certain sensitive personal information
                    </a>
                    &nbsp;only for certain purposes permitted under law
                  </p>
                </Col>
              </Row>

              <Row>
                <Col>
                  <img
                    id="logo"
                    width="30"
                    height="30"
                    alt="Privacy ToolBox"
                    src={imageBaseUrl + '/correct-my-info.svg'}
                    className="logo exercise-my-privacy-rights__bullet-logo"
                  />
                  <span
                    id="bullet-header"
                    className="bullet-header exercise-my-privacy-rights__bullet-header"
                  >Correct My Information
                  </span>
                  <br />
                  <p id="p-description" className="p-description">
                    You can ask that we correct inaccurate personal information about you
                  </p>
                </Col>
              </Row>

              <p className="p-foot-note">
                <span style={{ fontSize: '8px', float: 'left' }}>1</span>
                In accordance with state privacy laws, active Pro Xtra loyalty program accounts are exempt
                from deletion when you submit a Delete My Information request. If you would like to
                fully delete your Pro Xtra loyalty program account, please call 1-866-333-3551
                or email us at <a href="mailto:myinfo@homedepot.com">myinfo@homedepot.com</a>.
              </p>
              <p className="p-foot-note">
                <span style={{ fontSize: '8px', float: 'left' }}>2</span>
                We disclose your information in ways that do not directly identify you to
                select third parties to enhance your experiences with us, to improve our
                ability to serve you, to keep you aware of our products, services, and offers,
                and to tailor the offers and promotions displayed to you online by select third
                party advertisers to your interests. This disclosure may be considered a
                &quot;sale&quot; of your personal information. Read {' '}
                <LinkButton
                  onClick={handleTurnOnOverlay}
                > Opt Out of Sale
                </LinkButton> for more
                details.
              </p>
              <p className="p-foot-note">
                <span style={{ fontSize: '8px', float: 'left' }}>3</span>
                The right to opt out of Targeted Advertising is for cross-context behavioral
                advertising (CCBA) or advertising tracking an individual&apos;s
                activities across different websites, applications,
                and services to identify and present advertisements tailored to their behavior.
              </p>

              <Row>
                <Col className="embedded-accordion-wrapper">
                  <Accordion icon={{ position: 'left' }}>
                    <AccordionTitle>
                      <h1 style={{ fontSize: '18px' }}>Further information about submitting a request</h1>
                    </AccordionTitle>
                    <AccordionBody key="learnMore-0">
                      <p className="embedded-accordion-text">
                        After we process your request to delete your personal information or to opt out of sale,
                        you may still see general advertising regarding our products and services.
                        We may deliver advertising to a general audience or place advertising on websites,
                        mobile applications, and connected device applications that relates to our products
                        and services. For example, if you visit a do-it-yourself website, you may see general
                        advertising on that website that promotes our products and services related
                        to the do-it-yourself content.

                        <br />
                        <br />

                        To process your request, we will seek to identify the personal information
                        we hold that is linked to or associated with the personal information you
                        provide below.  If you have used different names, email addresses, phone
                        numbers, or addresses when interacting with us, some of the information we
                        hold may be linked to or associated with those other identifiers.  Therefore,
                        you may want to submit additional requests using the alternative
                        identifiers that you have used.
                      </p>
                    </AccordionBody>
                  </Accordion>
                </Col>
              </Row>
            </Col>
          </Row>

          <Row className="white margin-20">
            <Col className="content-summary">
              <h2 id="submit-form" style={{ marginBottom: '18px' }}>Exercise Your Privacy Rights</h2>

              <div className="sui-flex sui-flex-col">
                {
                  renderOptOutStatusBanner()
                }

              </div>
              <p className="p-form-instr">
                Complete the form below or call <a href="tel:+1-800-394-1326">1-800-394-1326</a> to submit your
                request. After you have submitted your request, you can revisit this page if you would like to
                submit another type of request.  The form requires that you provide your state of residence,
                first and last name, email address, and postal address.  And you must indicate that you are
                submitting the request as a natural person.  If you do not wish to provide your email address
                or if you do not have one, please call <a href="tel:+1-800-394-1326">1-800-394-1326</a> to
                submit your request.
              </p>
              <br />
              <p className="p-form-instr">
                For requests to access, delete, and correct your information, the information that you provide
                will be analyzed to determine whether we can reasonably verify your identity.  We may need
                to obtain additional information from you to process your request. For example, if we
                determine that the information provided is not sufficient for verification, you may be
                prompted to answer a few questions.
              </p>
              <br />
              <p className="p-form-instr">
                For requests to access, delete, and correct your information, we will also send an email to you,
                asking that you confirm that we have the correct email address within six (6) days of receiving
                the confirmation email or your request will expire.  If you do not see an email from us, please
                check your spam folder.
              </p>
              <br />
              <p className="p-form-instr">
                If you are an authorized agent acting on behalf of another individual, please send your request
                to <a href="mailto:myinfo@homedepot.com">myinfo@homedepot.com</a> and include the following information
                about you and the person on
                whose behalf you are submitting the request: full name, mailing address, email address, and phone
                number. You should also provide proof of your authorization to act on the other person&apos;s behalf. We
                will contact you for additional information once your request has been received.
              </p>
              <br />
              <p id="p-form-instr">
                You also can Opt Out of financial incentive programs we may offer. Please see the terms of
                the programs you join for a description of the financial incentives, including the
                categories of personal information that are collected, information about
                how personal information is valued, and instructions on how to opt in or withdraw from that
                program.  The terms for our Pro Xtra program can be found
                here:  <a href="https://www.homedepot.com/c/ProXtra_TermsandConditions"> Pro Xtra</a>
              </p>
              <br />
              {isMounted ? (
                <Suspense fallback={<div>Loading form...</div>}>
                  <ConsumerPrivacyActForm recaptchaKey={recaptchaKey} />
                </Suspense>
              ) : (
                <div>Loading form...</div>
              )}
            </Col>
          </Row>
          <BackToTop />
        </div>
      </div>

    </div>
  );
};

ExerciseMyPrivacyRights.propTypes = {
  recaptchaKey: string
};

ExerciseMyPrivacyRights.defaultProps = {
  recaptchaKey: ''
};
